/**
 * The alerts of Milk Flow (AP-4, Ronald on the decision sheet, 18 October 2026: on the screen only).
 *
 * On To do only (SC-5, 19 October 2026): what is past and still open under Overdue (LV-3), what is at risk in the
 * next 48 hours under its own heading, each with the day and time, what is at risk, the litres and the people who
 * hold the role that acts on it today. Every other screen carries one line with the count and a link to To do.
 * Every alert is critical. The people come from the platform's roles, never from this code.
 */
import { Badge, Section, Table } from "@depicomnlbv/platform-ui";

import { Link } from "react-router-dom";

import type { TimelineAlert } from "../api/milkFlow";
import { formatLitres } from "../time";

const WEEKDAY = new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", weekday: "short", day: "numeric", month: "short" });
const dayOf = (day: string) => WEEKDAY.format(new Date(`${day}T00:00:00Z`));

/** What is at risk, in the plant's words. */
export function alertWhat(a: TimelineAlert): string {
  switch (a.kind) {
    case "batch_short":
      return a.sequence ? `Batch ${a.sequence} not covered` : "Batch not covered";
    case "barrels_short":
      return "Barrels short";
    case "tanker_late":
      return a.source === "own" ? "Our tanker late" : "J.H. Willis tanker late";
    case "documents_missing":
      return `Delivery notes missing, ${a.loads ?? 0} ${a.loads === 1 ? "load" : "loads"}`;
    case "calendar":
      // PC-5: the cheese plan holds more batches than the production calendar allows.
      return `Plan ${a.batches_planned ?? "?"}, calendar allows ${a.batches_allowed ?? 0}`;
    case "thermise":
      // TH-2: milk the plan shows will pass L-5, from the first day it is known, with the day it came in.
      return a.came_in && a.by ? `Thermise milk of ${dayOf(a.came_in)}, by ${dayOf(a.by)}` : "Thermise";
    case "spot_sale":
      // TH-3: milk too old even thermised.
      return "Spot sale needed";
    case "not_placed":
      // US-11: milk the factory did not order and nobody has traded yet, a barrel or more within 48 hours.
      return "Milk not placed";
  }
}

export function MilkFlowAlerts({
  alerts,
  title = "Within 48 hours",
  caption = "Risks of the next 48 hours",
}: {
  alerts: TimelineAlert[];
  title?: string;
  caption?: string;
}) {
  if (alerts.length === 0) return null;
  return (
    <Section flush title={title}>
      <div className="mf-scroll">
        <Table caption={caption} captionHidden className="milk-flow-table mf-alerts">
          <thead>
            <tr>
              <th scope="col">Day</th>
              <th scope="col">Time</th>
              <th scope="col">At risk</th>
              <th className="dpc-table__num" scope="col">Litres</th>
              <th scope="col">Who acts</th>
            </tr>
          </thead>
          <tbody>
            {alerts.map((a, i) => (
              <tr key={`${a.kind}-${a.day}-${a.sequence ?? i}`}>
                <th scope="row">{dayOf(a.day)}</th>
                <td>{a.at ? a.at.slice(11, 16) : <span className="nothing">—</span>}</td>
                <td>
                  <Badge tone="critical">{alertWhat(a)}</Badge>
                </td>
                <td className="dpc-table__num">
                  {a.litres === null ? <span className="nothing">—</span> : formatLitres(a.litres)}
                </td>
                <td>{a.people.length > 0 ? a.people.join(", ") : <span className="nothing">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </Section>
  );
}

/** An alert on a day already past: a delivery note still missing (LV-3, Ronald on the sheet, 19 October 2026). */
export const isOverdue = (a: TimelineAlert, today: string) => a.day < today;

/**
 * The alerts live on To do only (SC-5, Ronald on the decision sheet, 19 October 2026). Every other screen
 * carries this one line, with the count and a link, and nothing when there is nothing to do.
 */
export function AlertsLine({ alerts }: { alerts: TimelineAlert[] }) {
  if (alerts.length === 0) return null;
  return (
    <p className="mf-alerts-line" role="status">
      <Link to="/to-do">
        <Badge tone="critical">{`${alerts.length} open on To do`}</Badge>
      </Link>
    </p>
  );
}

/** To do's two tables: what is past and still missing, then what is at risk in the next 48 hours (LV-3). */
export function ToDoAlerts({ alerts, today }: { alerts: TimelineAlert[]; today: string }) {
  const overdue = alerts.filter((a) => isOverdue(a, today));
  const coming = alerts.filter((a) => !isOverdue(a, today));
  return (
    <>
      <MilkFlowAlerts alerts={overdue} caption="Past days still open" title="Overdue" />
      <MilkFlowAlerts alerts={coming} title="Within 48 hours" />
    </>
  );
}
